import { useEffect, useState } from 'react';
import { api } from '../api';
import { useToast, Modal } from './shared';

export const TYPES = ['clinic', 'hospital', 'doctor', 'company', 'other'];
export const TYPE_BADGE = { clinic: 'badge-blue', hospital: 'badge-red', doctor: 'badge-green', company: 'badge-yellow', other: 'badge-gray' };
export const STATUS = { active: 'نشط', lead: 'محتمل', inactive: 'متوقف' };
export const STATUS_BADGE = { active: 'badge-green', lead: 'badge-yellow', inactive: 'badge-gray' };
export const SPECIALTY = { physio: 'علاج طبيعي', aesthetics: 'تجميل', slimming: 'تخسيس', mixed: 'متنوع', other: 'أخرى' };
export const PAYMENT = { cash: 'كاش', monthly: 'شهري', contract: 'عقد' };
export const LOC_SOURCE = { pin: 'لوكيشن (pin)', address: 'من العنوان', manual: 'يدوي' };
export const GOVERNORATES = ['القاهرة', 'الجيزة', 'القليوبية', 'الإسكندرية', 'المنوفية', 'الشرقية', 'الدقهلية', 'الغربية', 'البحيرة', 'كفر الشيخ',
  'دمياط', 'بورسعيد', 'الإسماعيلية', 'السويس', 'الفيوم', 'بني سويف', 'المنيا', 'أسيوط', 'سوهاج', 'قنا', 'الأقصر', 'أسوان',
  'البحر الأحمر', 'مطروح', 'شمال سيناء', 'جنوب سيناء', 'الوادي الجديد'];

// Google Maps link for a client (saved link first, else coordinates)
export const mapLink = c => c.maps_url || (c.lat != null && c.lng != null && c.lat !== '' && c.lng !== '' ? `https://www.google.com/maps?q=${c.lat},${c.lng}` : null);

export function MapButton({ client, small }) {
  const href = mapLink(client);
  if (!href) return null;
  return <a className={`btn btn-ghost ${small ? 'btn-sm' : ''}`} href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>🗺️ {small ? '' : 'افتح في الخريطة'}</a>;
}

const FIELDS = ['name', 'type', 'phone', 'notes', 'address', 'governorate', 'area', 'district', 'landmark', 'lat', 'lng', 'maps_url', 'location_source',
  'contact_name', 'contact_role', 'phone2', 'whatsapp', 'email', 'specialty', 'parent_client_id', 'client_code', 'status',
  'assigned_engineer', 'source', 'preferred_visit_time', 'payment_terms', 'tags'];

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="section-title">{title}</div>
      {children}
    </div>
  );
}

export default function ClientForm({ initial = {}, onSave, onClose }) {
  const [form, setForm] = useState(() => {
    const f = { type: 'clinic', status: 'active' };
    FIELDS.forEach(k => { f[k] = initial[k] ?? f[k] ?? ''; });
    return f;
  });
  const [clients, setClients] = useState([]);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.getClients().then(setClients).catch(() => {});
    api.getClientFacets().then(setFacets).catch(() => {});
  }, []);

  function useMyLocation() {
    if (!navigator.geolocation) return toast('المتصفح مش بيدعم تحديد الموقع', 'error');
    navigator.geolocation.getCurrentPosition(
      p => setForm(f => ({ ...f, lat: p.coords.latitude.toFixed(7), lng: p.coords.longitude.toFixed(7), location_source: 'pin' })),
      () => toast('مقدرناش نجيب الموقع', 'error'), { enableHighAccuracy: true, timeout: 10000 });
  }

  async function submit() {
    if (!form.name.trim()) return toast('الاسم مطلوب', 'error');
    setLoading(true);
    try {
      // send everything on edit (so cleared fields get cleared); skip blanks on create
      const body = {};
      FIELDS.forEach(k => { if (initial.id || form[k] !== '') body[k] = form[k]; });
      if (!initial.id && !form.client_code) delete body.client_code;
      // a new Maps link should re-derive the coordinates unless they were edited by hand
      if (initial.id && form.maps_url && form.maps_url !== (initial.maps_url || '')
        && String(form.lat) === String(initial.lat ?? '') && String(form.lng) === String(initial.lng ?? '')) { delete body.lat; delete body.lng; }
      await onSave(body);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }

  const dl = (id, values) => <datalist id={id}>{(values || []).map(v => <option key={v} value={v} />)}</datalist>;
  const txt = (k, label, props = {}) => (
    <div className="field"><label>{label}</label><input className="input" value={form[k]} onChange={set(k)} {...props} /></div>
  );
  const sel = (k, label, opts, blank = true) => (
    <div className="field"><label>{label}</label>
      <select className="select" value={form[k]} onChange={set(k)}>
        {blank && <option value="">—</option>}
        {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select></div>
  );

  return (
    <Modal width={760} title={initial.id ? `تعديل ${initial.name}` : 'عميل جديد'} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading}>{loading ? '...' : 'حفظ'}</button>
      </>
    }>
      <div dir="rtl">
        <div className="field"><label>الاسم *</label>
          <input className="input" value={form.name} onChange={set('name')} placeholder="مثلاً: مركز الربيع للعلاج الطبيعي" autoFocus /></div>
        <div className="grid-3">
          {sel('type', 'النوع', Object.fromEntries(TYPES.map(t => [t, t])), false)}
          {sel('status', 'الحالة', STATUS, false)}
          {txt('client_code', 'كود العميل', { placeholder: initial.id ? '' : 'تلقائي', dir: 'ltr' })}
        </div>

        <Section title="📍 الموقع">
          <div className="grid-3">
            <div className="field"><label>المحافظة</label>
              <input className="input" list="gov-list" value={form.governorate} onChange={set('governorate')} />
              {dl('gov-list', [...new Set([...(facets.governorate || []), ...GOVERNORATES])])}</div>
            <div className="field"><label>المركز / المدينة</label>
              <input className="input" list="area-list" value={form.area} onChange={set('area')} />{dl('area-list', facets.area)}</div>
            <div className="field"><label>الحي / المنطقة</label>
              <input className="input" list="district-list" value={form.district} onChange={set('district')} />{dl('district-list', facets.district)}</div>
          </div>
          {txt('address', 'العنوان بالتفصيل', { placeholder: 'شارع، رقم، دور' })}
          {txt('landmark', 'علامة مميزة', { placeholder: 'مثلاً: خلف المحجوب للسيراميك' })}
          <div className="field"><label>لينك جوجل ماب</label>
            <input className="input" dir="ltr" value={form.maps_url} onChange={set('maps_url')} placeholder="https://maps.app.goo.gl/..." />
            <div className="text-xs text-dim mt-4">لو اللينك فيه إحداثيات، lat/lng بيتملوا لوحدهم بعد الحفظ.</div></div>
          <div className="grid-3">
            {txt('lat', 'Lat', { dir: 'ltr', inputMode: 'decimal' })}
            {txt('lng', 'Lng', { dir: 'ltr', inputMode: 'decimal' })}
            {sel('location_source', 'مصدر الموقع', LOC_SOURCE)}
          </div>
          <div className="flex gap-8">
            <button type="button" className="btn btn-ghost btn-sm" onClick={useMyLocation}>📍 موقعي الحالي</button>
            <MapButton client={form} small={false} />
          </div>
        </Section>

        <Section title="📞 التواصل">
          <div className="grid-2">
            {txt('contact_name', 'اسم المسئول')}
            <div className="field"><label>وظيفته</label>
              <input className="input" list="role-list" value={form.contact_role} onChange={set('contact_role')} placeholder="دكتور / مدير / سكرتيرة" />
              {dl('role-list', [...new Set([...(facets.contact_role || []), 'دكتور', 'مدير', 'سكرتيرة', 'مالك'])])}</div>
          </div>
          <div className="grid-3">
            {txt('phone', 'التليفون', { dir: 'ltr', placeholder: '01xxxxxxxxx' })}
            {txt('phone2', 'تليفون تاني', { dir: 'ltr' })}
            {txt('whatsapp', 'واتساب (لو مختلف)', { dir: 'ltr' })}
          </div>
          {txt('email', 'الإيميل', { dir: 'ltr', type: 'email' })}
        </Section>

        <Section title="💼 العمل">
          <div className="grid-3">
            {sel('specialty', 'التخصص', SPECIALTY)}
            {sel('payment_terms', 'طريقة الدفع', PAYMENT)}
            <div className="field"><label>المهندس المسئول</label>
              <input className="input" list="eng-list" value={form.assigned_engineer} onChange={set('assigned_engineer')} />{dl('eng-list', facets.assigned_engineer)}</div>
          </div>
          <div className="grid-2">
            <div className="field"><label>فرع من (سلسلة)</label>
              <select className="select" value={form.parent_client_id} onChange={set('parent_client_id')}>
                <option value="">— مش فرع —</option>
                {clients.filter(c => c.id !== initial.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="field"><label>جه منين</label>
              <input className="input" list="src-list" value={form.source} onChange={set('source')} placeholder="إحالة، إعلان، زيارة ..." />
              {dl('src-list', [...new Set([...(facets.source || []), 'إحالة', 'إعلان', 'زيارة', 'واتساب', 'موقع'])])}</div>
          </div>
          <div className="grid-2">
            {txt('preferred_visit_time', 'أنسب وقت للزيارة', { placeholder: 'مثلاً: السبت والتلات بعد ٢' })}
            {txt('tags', 'تاجات', { placeholder: 'مفصولة بفواصل' })}
          </div>
          <div className="field"><label>ملاحظات</label>
            <textarea className="input" value={form.notes} onChange={set('notes')} rows={2} /></div>
        </Section>
      </div>
    </Modal>
  );
}
